import BodyContainer from "@/components/body/BodyContainer";
import Labs from "@/components/body/Labs";
import PatientDetailAttending from "@/components/census/PatientDetailAttending";
import Alerts from "@/components/shared/Alerts";
import {
  currentlyScrolling,
  registerForScrollingStop
} from "@/components/shared/AsyncQueue";
import SparkLine, {
  SparklineConfig
} from "@/components/shared/chart/SparkLine";
import { localNow } from "@/filters/DateAndTime";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import {
  createAllergies,
  createInfusions
} from "@/store/modules/patient/getters";
import { Patient } from "@/store/modules/patient/types";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { pointsInRange } from "../shared/chart/ChartUtils";
import InfusionList from "../shared/InfusionList";
import { Colors } from "../shared/util";

type Going = "in" | "out";

const ATTENDING_VIEW_BODY_REFRESH_MINUTES = 2 * 60 * 1000;

@Component({
  components: {
    Alerts,
    BodyContainer,
    PatientDetailAttending,
    InfusionList,
    Labs,
    SparkLine
  }
})
export default class AttendingCard extends Vue {
  @Action("fetchBodyData", patientNamespace)
  fetchBodyData!: Function;

  @Getter("darkMode", navigationNamespace)
  dark!: boolean;

  @Getter("area", patientNamespace)
  area!: string;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Prop()
  patient!: Patient;

  age: any = null;
  waitingForScrollingCallback: boolean = false;

  startPolling() {
    if (this.interval) {
      return;
    }
    this.onScreen = true;

    const now = localNow();
    const timeSinceLastStart = now.diff(this.age) || 0;

    if (!this.age || timeSinceLastStart > ATTENDING_VIEW_BODY_REFRESH_MINUTES) {
      this.getBody();
    }
    this.age = now;
    this.interval = setInterval(() => {
      this.getBody();
    }, ATTENDING_VIEW_BODY_REFRESH_MINUTES);
  }

  stopPolling() {
    if (!this.interval) {
      return;
    }
    this.onScreen = false;

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  interval: any;

  removeParts = [
    "brainText",
    "lungsText",
    "heartText",
    "lungRateOnlyText",
    "coughText",
    "heartVolumeText",
    "leftKidneyText",
    "rightKidneyText",
    "eblText",
    "cough",
    "lungRateOnlyText",
    "coughText",
    "mouthAndChin",
    "activity"
  ];

  waypointConfig = {
    active: true,
    callback: this.onWaypoint,
    options: {
      root: document.getElementById("censusContent"),
      rootMargin: "0px",
      threshold: [0.1, 0.9]
    }
  };
  onScreen = false;
  going: Going | null = null;

  async onWaypoint({ going }: { going: Going; direction: string }) {
    this.going = going;

    if (this.waitingForScrollingCallback) {
      return;
    }

    const scrolling = currentlyScrolling();
    if (scrolling) {
      this.waitingForScrollingCallback = true;

      registerForScrollingStop(() => {
        this.waitingForScrollingCallback = false;
        this.startOrStopPolling();
      });
    } else {
      this.startOrStopPolling();
    }
  }

  startOrStopPolling() {
    if (this.going === "in") {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  getBody() {
    this.fetchBodyData(this.patient.id);
  }

  get allergies() {
    return createAllergies(this.patient);
  }

  get infusions() {
    return createInfusions(this.patient);
  }

  beforeDestroy() {
    this.stopPolling();
  }

  @Getter("config", patientNamespace)
  config!: any;

  get sparklineConfigs(): Array<SparklineConfig> {
    const allDataPoints = this.graphs;
    const gradients = this.gradients;
    const merged = allDataPoints.map(
      (dataPoints: Array<{ time: string; value: number }>, index) => {
        const data = dataPoints.map(d => {
          return { x: new Date(d.time).getTime(), y: d.value };
        });
        const gradientRanges = gradients[index];
        const filteredPoints = pointsInRange(
          data,
          this.config.census.attending.sparkline[index].range
        );
        return {
          data: filteredPoints,
          gradientRanges,
          minVisibleY: this.config.census.attending.sparkline[index].min,
          maxVisibleY: this.config.census.attending.sparkline[index].max
        };
      }
    );
    return merged;
  }

  get graphs() {
    const output: Array<any> = [];
    this.config.census.attending.sparkline.forEach((config: any) => {
      const key = config.key;
      if (!this.patient.body.graph[key]) {
        output.push([]);
        return;
      }
      const data = this.patient.body.graph[key];
      output.push(data);
    });

    return output;
  }

  get gradients() {
    const keyedVarLevels = {} as any;
    this.vars.varLevels.forEach((vl: any) => {
      keyedVarLevels[vl.variable] = vl;
    });
    return this.config.census.attending.sparkline.map((s: any) => {
      const key = s.levelKey || s.key;
      const levels = keyedVarLevels[key];
      if (!levels) {
        return [
          {
            min: Number.NEGATIVE_INFINITY,
            max: Number.POSITIVE_INFINITY,
            color: this.dark ? Colors.White : Colors.Black
          }
        ];
      } else {
        return [
          {
            min: Number.NEGATIVE_INFINITY,
            max: levels.level1,
            color: Colors.Red
          },
          { min: levels.level1, max: levels.level2, color: Colors.Yellow },
          { min: levels.level2, max: levels.level3, color: Colors.Green },
          { min: levels.level3, max: levels.level4, color: Colors.Yellow },
          {
            min: levels.level4,
            max: Number.POSITIVE_INFINITY,
            color: Colors.Red
          }
        ];
      }
    });
  }

  get yPoints() {
    return this.graphs.map(graph => {
      if (!graph.length) {
        return null;
      }
      return graph[graph.length - 1].value;
    });
  }

  calculateStyle(i: number, point: number) {
    let found = null as any;
    this.gradients[i].forEach((g: any) => {
      if (point > g.min && point <= g.max) {
        found = g;
      }
    });
    if (found) {
      return `color: ${found.color}`;
    }
    return "";
  }
}
